import { trimTrailingChar, trimTrailingSlashes } from "./utils/safeString";
import { randomStringFromAlphabet } from "./utils/random";

const CRA = {
  KC_URL: trimTrailingSlashes(process.env.REACT_APP_KC_URL || "http://localhost:8085"),
  KC_REALM: process.env.REACT_APP_KC_REALM || "voice-bot",
  KC_CLIENT_ID: process.env.REACT_APP_KC_CLIENT_ID || "voice-bot-spa",
  KC_REDIRECT_URI: process.env.REACT_APP_KC_REDIRECT_URI || `${window.location.origin}/auth/callback`,
};

const KC = {
  url: CRA.KC_URL,
  realm: CRA.KC_REALM,
  clientId: CRA.KC_CLIENT_ID,
  redirectUri: CRA.KC_REDIRECT_URI,
};

const endpoints = {
  authorize: `${KC.url}/realms/${KC.realm}/protocol/openid-connect/auth`,
  token: `${KC.url}/realms/${KC.realm}/protocol/openid-connect/token`,
  logout: `${KC.url}/realms/${KC.realm}/protocol/openid-connect/logout`,
};

function b64url(uint8) {
  const base64 = trimTrailingChar(btoa(String.fromCharCode.apply(null, [...uint8])), "=");
  return base64.split("+").join("-").split("/").join("_");
}

async function sha256(str) {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest("SHA-256", enc.encode(str));
  return new Uint8Array(buf);
}

function randomString(len = 43) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  return randomStringFromAlphabet(len, chars);
}

export async function login() {
  const state = randomString(16);
  const codeVerifier = randomString(64);
  const codeChallenge = b64url(await sha256(codeVerifier));

  sessionStorage.setItem("kc_state", state);
  sessionStorage.setItem("kc_code_verifier", codeVerifier);

  const params = new URLSearchParams({
    client_id: KC.clientId,
    response_type: "code",
    scope: "openid profile email",
    redirect_uri: KC.redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
    state,
  });

  window.location = `${endpoints.authorize}?${params.toString()}`;
}

export async function handleRedirect() {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  if (!code) return false;

  const marker = `kc_handled_${state || "nostate"}`;
  const already = sessionStorage.getItem(marker);
  if (already === code) return true;
  sessionStorage.setItem(marker, code);

  const cleanup = () => {
    ["code", "state", "session_state", "iss"].forEach(p => url.searchParams.delete(p));
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    sessionStorage.removeItem("kc_state");
    sessionStorage.removeItem("kc_code_verifier");
  };

  let success = false;
  try {
    const expectState = sessionStorage.getItem("kc_state");
    const codeVerifier = sessionStorage.getItem("kc_code_verifier");
    if (!expectState || state !== expectState) throw new Error("Bad OIDC state");

    const body = new URLSearchParams({
      grant_type: "authorization_code",
      client_id: KC.clientId,
      code,
      redirect_uri: KC.redirectUri,
      code_verifier: codeVerifier,
    });

    const r = await fetch(endpoints.token, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
    if (!r.ok) {
      const txt = await r.text();
      throw new Error(`Token exchange failed: ${r.status} ${txt}`);
    }

    const tok = await r.json();
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = now + (tok.expires_in || 300) - 30;

    localStorage.setItem("kc_tokens", JSON.stringify({
      access_token: tok.access_token,
      refresh_token: tok.refresh_token,
      id_token: tok.id_token,
      expires_at: expiresAt,
    }));

    success = true;
    cleanup();
    window.location.replace("/");
    return true;
  } catch (e) {
    console.error("[OIDC] exchange failed:", e);

    return false;
  } finally {
    if (success && url.searchParams.has("code")) {

      cleanup();
    }
  }
}

export function getTokens() {
  const raw = localStorage.getItem("kc_tokens");
  return raw ? JSON.parse(raw) : null;
}
export function getAccessToken() {
  return getTokens()?.access_token || null;
}

export async function refreshIfNeeded(force = false) {
  const t = getTokens();
  if (!t) return false;

  const now = Math.floor(Date.now() / 1000);
  if (!force && typeof t.expires_at === 'number' && now < t.expires_at) return false;

  if (!t.refresh_token) return false;

  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: KC.clientId,
    refresh_token: t.refresh_token,
  });

  let r;
  try {
    r = await fetch(endpoints.token, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body,
    });
  } catch {
    // Keep current session state on transient network/CORS failures.
    return false;
  }

  if (!r.ok) {
    // If access token is still valid, keep session and skip hard logout on refresh errors.
    const hasValidAccess =
      !!t.access_token &&
      typeof t.expires_at === "number" &&
      now < t.expires_at;

    // Clear tokens only for auth-invalid responses when no valid access token remains.
    if ((r.status === 400 || r.status === 401) && !hasValidAccess) {
      logout(false);
    }
    return false;
  }

  const tok = await r.json();
  const now2 = Math.floor(Date.now() / 1000);
  const expiresAt = now2 + (tok.expires_in || 300) - 30;

  localStorage.setItem("kc_tokens", JSON.stringify({
    access_token: tok.access_token,
    refresh_token: tok.refresh_token,
    id_token: tok.id_token || t.id_token,
    expires_at: expiresAt,
  }));

  return true;
}

export async function authedFetch(url, opts = {}) {
  try {
    await refreshIfNeeded();
  } catch {
  }

  const at = getAccessToken();
  const headers = {
    ...(opts.headers || {}),
    ...(at ? { Authorization: `Bearer ${at}` } : {}),
  };

  let res = await fetch(url, { ...opts, headers });

  if (res.status === 401) {
    let ok = false;
    try {
      ok = await refreshIfNeeded(true);
    } catch {
      ok = false;
    }
    if (ok) {
      const at2 = getAccessToken();
      const headers2 = { ...headers, ...(at2 ? { Authorization: `Bearer ${at2}` } : {}) };
      res = await fetch(url, { ...opts, headers: headers2 });
    }
  }

  return res;
}

export function isAuthenticated() {
  const t = getTokens();
  if (!t?.access_token) return false;
  const now = Math.floor(Date.now() / 1000);
  return typeof t.expires_at === 'number' && now < t.expires_at;
}

export function logout(hard = false) {
  const t = getTokens();
  localStorage.removeItem("kc_tokens");
  if (hard && t?.id_token) {
    const params = new URLSearchParams({
      client_id: KC.clientId,
      post_logout_redirect_uri: window.location.origin,
      id_token_hint: t.id_token,
    });
    window.location = `${endpoints.logout}?${params.toString()}`;
  }
}

function base64UrlToStr(b64url) {
  const b64 = String(b64url || "").split("-").join("+").split("_").join("/");
  const pad = b64.length % 4 === 2 ? "==" : b64.length % 4 === 3 ? "=" : "";
  return atob(b64 + pad);
}
export function getUserInfo() {
  const t = getTokens();
  if (!t?.id_token) return null;
  try {
    const payloadStr = base64UrlToStr(t.id_token.split(".")[1] || "");
    const p = JSON.parse(payloadStr);
    return {
      name: p.name || p.preferred_username || p.email || "Потребител",
      email: p.email || "",
      picture: p.picture || null,
      sub: p.sub,
    };
  } catch {
    return null;
  }
}
